import "./App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SiteLayout from "./components/site/SiteLayout";
import Home from "./pages/site/Home";
import { Features, Ims, BusinessTools, Pricing, About, Contact, Legal } from "./pages/site/Pages";
import Consultants from "./pages/site/Consultants";
import QordelAI from "./pages/app/QordelAI";
import { Login, Register, VerifyEmail, ForgotPassword, SetPassword } from "./pages/auth/AuthPages";
import AppLayout from "./components/app/AppLayout";
import ModulePage from "./pages/app/ModulePage";
import UsersPage from "./pages/app/Users";
import Billing, { PaymentSuccess } from "./pages/app/Billing";
import SettingsPage, { Support } from "./pages/app/Settings";
import Admin from "./pages/admin/Admin";
import { ERP_ROUTES, NxDashboard } from "./nestix/erpRoutes";
import PrintCalibrationRecord from "./nestix/pages/PrintCalibrationRecord";
import CustomerSatisfactionPublicSurvey from "./nestix/pages/CustomerSatisfactionPublicSurvey";
import PublicPOConfirm from "./nestix/pages/PublicPOConfirm";
import EmploymentApplicationForm from "./nestix/pages/EmploymentApplicationForm";

function Guard({ owner, children }) {
  const { session } = useAuth();
  const location = useLocation();
  if (session === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  const isOwner = session.user.role === "platform_owner";
  if (owner && !isOwner) return <Navigate to="/app" replace />;
  if (!owner && isOwner) return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/ims" element={<Ims />} />
            <Route path="/consultants" element={<Consultants />} />
            <Route path="/business-tools" element={<BusinessTools />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:doc" element={<Legal />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<SetPassword mode="reset" />} />
          <Route path="/accept-invite" element={<SetPassword mode="invite" />} />
          <Route path="/payment/success" element={<Guard><PaymentSuccess /></Guard>} />
          <Route path="/billing" element={<Navigate to="/app/billing" replace />} />
          <Route path="/app" element={<Guard><AppLayout /></Guard>}>
            <Route index element={<NxDashboard />} />
            <Route path="m/:key" element={<ModulePage />} />
            <Route path="ai" element={<QordelAI />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="support" element={<Support />} />
          </Route>
          <Route element={<Guard><AppLayout /></Guard>}>
            {ERP_ROUTES.map(([path, El]) => <Route key={path} path={path} element={<El />} />)}
          </Route>
          <Route path="/quality/calibration/print/:recordId" element={<Guard><PrintCalibrationRecord /></Guard>} />
          <Route path="/po/confirm/:token" element={<PublicPOConfirm />} />
          <Route path="/survey/customer-satisfaction/:token" element={<CustomerSatisfactionPublicSurvey />} />
          <Route path="/apply/employment-application" element={<EmploymentApplicationForm publicMode />} />
          <Route path="/admin" element={<Guard owner><Admin /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
